import type {MonthColumn} from "../../utils";
import {displayColumnsForTable} from "../../utils";
import type {PeriodicBalanceSheetViewRow} from "../../types";
import {EmptyTableBody, SkeletonAccountCell, SkeletonAmountCell} from "../skeletonParts.tsx";
import {AccountHeaderCell, MonthHeaderCells} from "../PeriodicStatementTable/MonthHeaderCells.tsx";
import {PeriodicTableShell} from "../PeriodicStatementTable/PeriodicTableShell.tsx";
import {
    MONTH_COLUMN_WIDTH,
    STICKY_COLUMN_WIDTH,
    SUMMARY_COLUMN_WIDTH,
} from "../PeriodicStatementTable/tableStyles.ts";
import {PeriodicBalanceSheetTableRow} from "./PeriodicBalanceSheetTableRow.tsx";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

const SKELETON_ROW_COUNT = 10;

interface PeriodicBalanceSheetTableProps {
    rows: PeriodicBalanceSheetViewRow[];
    columns: MonthColumn[];
    currency: string;
    legalEntityType: LegalEntityType;
    onToggleNode: (id: string) => void;
    onCellClick: (row: PeriodicBalanceSheetViewRow, periodIndex: number) => void;
    loading: boolean;
    skeletonMonthCount: number;
    emptyMessage?: string;
}

export function PeriodicBalanceSheetTable({
    rows,
    columns,
    currency,
    legalEntityType,
    onToggleNode,
    onCellClick,
    loading,
    skeletonMonthCount,
    emptyMessage,
}: PeriodicBalanceSheetTableProps) {
    const displayColumns = displayColumnsForTable(columns, loading, skeletonMonthCount);

    let body;
    if (loading) {
        body = Array.from({length: SKELETON_ROW_COUNT}, (_, rowIndex) => (
            <tr key={rowIndex} className="[&>*]:border-b [&>*]:border-b-gray-100">
                <SkeletonAccountCell rowIndex={rowIndex} />
                {[undefined, ...displayColumns].map((_, colIndex) => (
                    <SkeletonAmountCell
                        key={colIndex}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                        withLeftBorder={colIndex > 0}
                    />
                ))}
            </tr>
        ));
    } else if (rows.length === 0) {
        body = <EmptyTableBody colSpan={2 + displayColumns.length} message={emptyMessage} />;
    } else {
        body = rows.map((row) => (
            <PeriodicBalanceSheetTableRow
                key={row.id}
                row={row}
                columns={displayColumns}
                currency={currency}
                legalEntityType={legalEntityType}
                onToggleNode={onToggleNode}
                onCellClick={onCellClick}
            />
        ));
    }

    return (
        <PeriodicTableShell
            loading={loading}
            minWidth={STICKY_COLUMN_WIDTH + SUMMARY_COLUMN_WIDTH + displayColumns.length * MONTH_COLUMN_WIDTH}
            header={
                <thead>
                    <tr className="bg-gray-50 [&>*]:border-b [&>*]:border-b-gray-200">
                        <AccountHeaderCell />
                        <th className="px-3 py-3 text-right text-[12px] font-bold text-gray-900 whitespace-nowrap bg-[#F4FAFE]">
                            Beginbalans
                        </th>
                        <MonthHeaderCells
                            columns={displayColumns}
                            showPlaceholders={loading && columns.length === 0}
                            separateFirst
                        />
                    </tr>
                </thead>
            }
            body={body}
        />
    );
}
